import { useSceneEvents } from "@/components/common/scene-events-context";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { PointCloudObject } from "@/object-cache";
import { PointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import { useAppSelector } from "@/store/store-hooks";
import {
  computeColormapParameters,
  getAnalysisReferencePlane,
} from "@/utils/colormap-analysis-utils";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { AdaptivePointsMaterial } from "@faro-lotv/lotv";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Plane, Vector3 } from "three";
import { AnalysisLabel } from "./analysis-label";
import { AnalysisLabelMenu } from "./analysis-label-menu";

type ColormapAnalysisRendererProps = {
  /** Point cloud object used by analysis */
  pointCloud: PointCloudObject;

  /** The point cloud analysis to be rendered */
  analysis: PointCloudAnalysis;

  /**
   * Optional scale factor for the labels.
   * For example, a labelsScale of 0.2 will render labels at 20% of their default size.
   */
  labelsScale?: number;
};

/** @returns The renderer of colormap analysis on point cloud */
export function ColormapAnalysisRenderer({
  pointCloud,
  analysis,
  labelsScale,
}: ColormapAnalysisRendererProps): JSX.Element {
  const sceneEvents = useSceneEvents();

  // add dependency to worldMatrix to make sure to re-compute colormapParms if worldMatrix is changed
  const worldMatrix = useAppSelector(
    selectIElementWorldMatrix4(pointCloud.iElement.id),
  );

  useEffect(() => {
    const { material } = pointCloud;
    if (!(material instanceof AdaptivePointsMaterial)) {
      return;
    }

    const colormapParams = computeColormapParameters(pointCloud, analysis);
    if (!colormapParams) {
      return;
    }

    material.setColormap(colormapParams);
    // Sometime updating material does not trigger re-render PointCloudSubscene
    // Manually invalidate is required to make sure view is updated
    sceneEvents.invalidatePointCloudScene.emit();
    return () => {
      material.setColormap(undefined);
      sceneEvents.invalidatePointCloudScene.emit();
    };
  }, [
    analysis,
    pointCloud,
    sceneEvents.invalidatePointCloudScene,
    worldMatrix,
  ]);
  const labelContainer = useViewOverlayRef();

  const referencePlane = useMemo(() => {
    const plane = getAnalysisReferencePlane(analysis);
    return plane
      ? new Plane().setFromNormalAndCoplanarPoint(plane.normal, plane.point)
      : undefined;
  }, [analysis]);

  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(
    undefined,
  );
  const [menuPosition, setMenuPosition] = useState<Vector3 | undefined>(
    undefined,
  );

  const openContextMenu = useCallback((labelId: string, position: Vector3) => {
    setSelectedLabel(labelId);
    setMenuPosition(position);
  }, []);

  const closeMenu = useCallback(() => {
    setSelectedLabel(undefined);
    setMenuPosition(undefined);
  }, []);

  return (
    <>
      {
        // Render labels
        analysis.labels.map((label) =>
          referencePlane ? (
            <AnalysisLabel
              key={label.id}
              label={label}
              labelsScale={labelsScale}
              referencePlane={referencePlane}
              parentRef={labelContainer}
              openContextMenu={openContextMenu}
            />
          ) : null,
        )
      }
      {selectedLabel && menuPosition && (
        <AnalysisLabelMenu
          analysisId={analysis.id}
          labelId={selectedLabel}
          position={menuPosition}
          parentRef={labelContainer}
          closeMenu={closeMenu}
        />
      )}
    </>
  );
}
